/**
 * AppBadgeDiagnostics
 *
 * Zeigt eine Checkliste an, warum das App-Icon-Badge ggf. NICHT angezeigt wird.
 * Hilft Nutzer:innen zu verstehen, welche Voraussetzung auf ihrem Gerät noch fehlt.
 *
 * Prüfungen:
 *  1. Browser-Unterstützung für Badge API (`navigator.setAppBadge`)
 *  2. PWA-Installation (`display-mode: standalone` oder `navigator.standalone`)
 *  3. Notification-Permission
 *  4. Push-Subscription aktiv
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, AlertCircle, Smartphone, Info, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getPermissionStatus, isSubscribed as checkIsSubscribed } from '../lib/pushNotifications';
import { usePwaInstall } from '../hooks/usePwaInstall';

const Row = ({ status, label, hint }) => {
  const Icon = status === 'ok' ? CheckCircle2 : status === 'warn' ? AlertCircle : XCircle;
  const color = status === 'ok' ? 'text-green-600' : status === 'warn' ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
};

const getPlatform = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh/i.test(ua)) return 'macos';
  if (/Windows/i.test(ua)) return 'windows';
  return 'other';
};

export const AppBadgeDiagnostics = () => {
  const [state, setState] = useState({
    badgeApi: false,
    standalone: false,
    permission: 'default',
    subscribed: false,
    platform: 'other',
  });
  const { available: pwaAvailable, install: pwaInstall, ios: isIosSafari } = usePwaInstall();
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    (async () => {
      const subscribed = await checkIsSubscribed().catch(() => false);
      setState({
        badgeApi: typeof navigator !== 'undefined' && 'setAppBadge' in navigator,
        standalone: isStandalone(),
        permission: getPermissionStatus(),
        subscribed,
        platform: getPlatform(),
      });
    })();
  }, []);

  const platformTip = (() => {
    if (state.platform === 'ios') {
      if (!state.standalone) {
        return 'iPhone/iPad: Öffne diese Seite in Safari → „Teilen" → „Zum Home-Bildschirm" hinzufügen. Erst dann kann das App-Icon Badges anzeigen (iOS 16.4+).';
      }
      return 'iOS 16.4+ erforderlich. Wenn das Badge trotzdem nicht erscheint, prüfe in den iOS-Einstellungen: Mitteilungen → TeamHub → „Mitteilungen erlauben" + „Symbole".';
    }
    if (state.platform === 'android') {
      if (!state.standalone) return 'Android: Öffne in Chrome → Menü → „App installieren" oder „Zum Startbildschirm hinzufügen".';
      return 'Android Chrome unterstützt Icon-Badges – stelle sicher, dass „Benachrichtigungen anzeigen" in den App-Einstellungen aktiviert ist.';
    }
    if (state.platform === 'macos') {
      if (!state.standalone) return 'macOS: Öffne die App in Chrome oder Edge → URL-Leiste rechts → „App installieren" → das Icon erscheint dann im Dock und kann Badges anzeigen.';
      return 'Stelle sicher, dass die App im Dock ist (nicht nur als Browser-Tab geöffnet).';
    }
    if (state.platform === 'windows') {
      if (!state.standalone) return 'Windows: Öffne in Edge/Chrome → URL-Leiste rechts → „App installieren" → Badge erscheint dann in der Taskleiste.';
      return 'Windows-Taskleisten-Badge sollte erscheinen, wenn die PWA korrekt installiert ist.';
    }
    return '';
  })();

  return (
    <Card className="border-border/60" data-testid="badge-diagnostics">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Smartphone className="h-4 w-4 text-primary" />
          Badge-Diagnose
          <Badge variant="outline" className="text-xs">{state.platform}</Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          Warum erscheint (k)eine Zahl auf dem App-Icon?
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        <Row
          status={state.badgeApi ? 'ok' : 'warn'}
          label={`Browser unterstützt Icon-Badges${state.badgeApi ? '' : ' nicht'}`}
          hint={state.badgeApi
            ? 'navigator.setAppBadge ist verfügbar'
            : 'Im Browser-Tab kein Badge möglich. Auf iOS: nur als installierte PWA.'}
        />
        <Row
          status={state.standalone ? 'ok' : 'warn'}
          label={state.standalone ? 'App ist als PWA installiert' : 'Im normalen Browser-Tab geöffnet'}
          hint={state.standalone ? 'display-mode: standalone' : platformTip}
        />
        <Row
          status={state.permission === 'granted' ? 'ok' : state.permission === 'denied' ? 'fail' : 'warn'}
          label={
            state.permission === 'granted' ? 'Benachrichtigungen erlaubt' :
            state.permission === 'denied' ? 'Benachrichtigungen blockiert' :
            'Benachrichtigungen noch nicht erlaubt'
          }
          hint={state.permission !== 'granted' && 'Aktiviere oben den „Browser Push"-Schalter und erlaube den System-Dialog.'}
        />
        <Row
          status={state.subscribed ? 'ok' : 'warn'}
          label={state.subscribed ? 'Push-Subscription aktiv' : 'Keine Push-Subscription'}
          hint={!state.subscribed && 'Ohne Subscription empfängt das Gerät keine Live-Pushes. Aktiviere den Toggle oben.'}
        />

        {(!state.standalone && state.platform !== 'other') && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
            <div className="text-amber-900 dark:text-amber-200 flex-1">
              <p className="font-medium mb-0.5">So bekommst du das App-Icon-Badge:</p>
              <p>{platformTip}</p>
              {pwaAvailable && (
                <Button
                  size="sm"
                  className="mt-2 gap-1"
                  onClick={async () => {
                    setInstalling(true);
                    try {
                      const { outcome } = await pwaInstall();
                      if (outcome === 'accepted') {
                        toast.success('App wird installiert – nach Abschluss findest du sie auf deinem Homescreen / Dock.');
                      } else if (outcome === 'dismissed') {
                        toast.info('Installation abgebrochen');
                      }
                    } finally {
                      setInstalling(false);
                    }
                  }}
                  disabled={installing}
                  data-testid="pwa-install-btn"
                >
                  <Download className="h-3.5 w-3.5" />
                  {installing ? 'Wird installiert …' : 'Jetzt als App installieren'}
                </Button>
              )}
              {!pwaAvailable && !isIosSafari && state.platform !== 'ios' && (
                <p className="mt-2 text-[11px] opacity-80">
                  💡 Falls der Install-Button nicht erscheint: Klicke in der Adressleiste deines Browsers (Chrome/Edge) auf das kleine „App installieren"-Icon rechts.
                </p>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-3">
          💡 Im Browser-Tab funktioniert der <strong>Favicon-Badge + Tab-Titel</strong> ({'('}3{')'} TeamHub) immer – unabhängig von PWA-Installation.
        </p>
      </CardContent>
    </Card>
  );
};

export default AppBadgeDiagnostics;
